import React from 'react';
import { Calendar, Image, Package, Infinity, Upload } from 'lucide-react';
import Button from '../ui/Button';
import { useGetCurrentUserQuery, useGetUserStatisticsQuery } from '../../store/services/api';
import { logger } from '../../utils/logger';

interface UserQuotaOverviewProps {
  showBuyCredits?: boolean;
  onBuyCreditsClick?: () => void;
  className?: string;
}

const UserQuotaOverview: React.FC<UserQuotaOverviewProps> = ({
  showBuyCredits = true,
  onBuyCreditsClick,
  className = ''
}) => {
  const { data: user } = useGetCurrentUserQuery();
  const { data: statistics } = useGetUserStatisticsQuery();

  // API yanıtını logla
  React.useEffect(() => {
    logger.debug('User statistics:', statistics);
  }, [statistics]);

  const handleBuyCreditsClick = () => {
    // Custom event'i tetikle
    const event = new CustomEvent('openBuyCredits');
    document.dispatchEvent(event);
    
    // Props'tan gelen click handler'ı da çağır
    if (onBuyCreditsClick) {
      onBuyCreditsClick();
    }
  };

  return (
    <div className={`grid md:grid-cols-2 gap-4 ${className}`}>
      {/* Image Credits Card */}
      <div className="bg-white rounded-lg p-4 border border-gray-100">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-md bg-[#FFEEEE] flex items-center justify-center">
            <Image className="w-4 h-4 text-[#FF4444]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-0.5">
              {statistics?.remaining_photo_quota || 0}
            </h2>
            <p className="text-xs text-gray-500">Image Credits Left</p>
          </div>
        </div>
        <div className="space-y-1.5 mb-4">
          <div className="w-full h-1.5 bg-gray-50 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#FF4444] rounded-full transition-all duration-300" 
              style={{ width: `${statistics?.usage_percentage || 0}%` }}
            />
          </div>
          <div className="text-right text-xs text-gray-500">
            {statistics?.uploaded_photos || 0} / {statistics?.total_photo_quota || 0} used
          </div>
        </div>
        {showBuyCredits && (
          <Button 
            variant="outline" 
            fullWidth
            onClick={handleBuyCreditsClick}
            className="text-sm h-9"
          >
            <Package className="w-4 h-4 mr-2" />
            Buy More Credits
          </Button>
        )}
      </div>

      {/* Events Info Card */}
      <div className="bg-white rounded-lg p-4 border border-gray-100">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-md bg-[#FFEEEE] flex items-center justify-center">
            <Calendar className="w-4 h-4 text-[#FF4444]" />
          </div>
          <div>
            <h2 className="text-lg font-medium mb-0.5">Event Limit</h2>
            <p className="text-xs text-gray-500">
              {user?.event_limit === -1 ? 'Unlimited' : user?.event_limit || 1} events available
            </p>
          </div>
        </div>
        <div className="p-3 bg-gray-50 rounded-md">
          <div className="flex items-start gap-2">
            <Infinity className="w-4 h-4 text-[#FF4444] mt-0.5" />
            <div>
              <p className="text-sm font-medium mb-0.5">Event capacity</p>
              <p className="text-xs text-gray-500">
                Unlimited events with 3 months hosting
              </p>
            </div>
          </div>
          <div className="mt-3 flex items-start gap-2">
            <Upload className="w-4 h-4 text-[#FF4444] mt-0.5" />
            <div>
              <p className="text-sm font-medium mb-0.5">Image credits required</p>
              <p className="text-xs text-gray-500">Each uploaded image consumes one image credit</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserQuotaOverview;
